import React, { useEffect, useState, useMemo } from 'react';
import { Card, Modal, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation, withTranslation } from 'react-i18next';
import { BiSort, BiSortUp, BiSortDown, BiInfoCircle } from 'react-icons/bi';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import useBasicGraphCard from '../../../core/hooks/useBasicGraphCard';
import Utils from '../../../core/utilities/Utils';
import ChartMenu from '../../../components/ChartMenu';
import ESGAnalyticsDPBreakdownModal from './ESGAnalyticsDPBreakdownModal';
import { buildNullDatapointBreakdownPayload } from './esgAnalyticsNullBreakdown';
import './ESGAnalyticsStackedBarChart.scss';

// Round marker above bars; keep “!” readable at ~font r * 1.25
const NULL_MARKER_RADIUS = 6;
// Space between bar top and bottom of red circle — large enough to clear rotated datalabels
const NULL_MARKER_GAP = 20;

/** Screen-space position of every red "!" marker currently drawn, shared by the draw plugin and click/hover hit-testing. */
function computeNullMarkerGeometry(chart) {
  const { data } = chart;
  if (!data?.datasets?.length) return [];
  const markers = [];
  data.datasets.forEach((dataset, datasetIndex) => {
    const flags = dataset.nullPointFlags;
    if (!Array.isArray(flags)) return;
    const meta = chart.getDatasetMeta(datasetIndex);
    if (meta.hidden) return;
    flags.forEach((isNull, dataIndex) => {
      if (!isNull) return;
      const el = meta.data?.[dataIndex];
      if (!el) return;
      const x = el.x;
      const base = typeof el.base === 'number' ? el.base : (el.getProps ? el.getProps(['base'], true).base : el.y);
      const top = typeof el.y === 'number' ? el.y : (el.getProps ? el.getProps(['y'], true).y : null);
      const barValue = dataset?.data?.[dataIndex] ?? 0;
      if (typeof x !== 'number' || typeof base !== 'number') return;
      const anchorY = barValue !== 0 && typeof top === 'number' ? top : base;
      // cy + r = anchorY - gapBarTopToCircleBottom → circle sits above datalabel band at bar top
      const cy = anchorY - NULL_MARKER_RADIUS - NULL_MARKER_GAP;
      markers.push({ x, cy, r: NULL_MARKER_RADIUS, datasetIndex, dataIndex, dataset });
    });
  });
  return markers;
}

/** Nearest marker under a canvas-space point, or null. Used to hit-test clicks/hover against the drawn circles. */
function findNullMarkerAtPoint(chart, x, y, hitPadding = 4) {
  if (x == null || y == null) return null;
  const markers = computeNullMarkerGeometry(chart);
  return markers.find((marker) => Math.hypot(x - marker.x, y - marker.cy) <= marker.r + hitPadding) || null;
}

/** Renders a round red "!" for explicit nulls (replaces the old datalabels triangle character). */
const ESG_EXCLAMATION_NULL_MARKER_PLUGIN = {
  id: 'esgAnalyticsStackedBarNullExclamation',
  afterDatasetsDraw: (chart) => {
    if (!chart?.options?.plugins?.esgNullMarkers?.enabled) {
      return;
    }
    const markers = computeNullMarkerGeometry(chart);
    if (!markers.length) return;

    const { ctx } = chart;
    ctx.save();
    markers.forEach(({ x, cy, r }) => {
      ctx.beginPath();
      ctx.arc(x, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = '#dc2626';
      ctx.fill();

      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(r * 1.25)}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', x, cy);
    });
    ctx.restore();
  },
};

const ESGAnalyticsStackedBarChart = ({ t, dataViewData, configRows, allConfigRows, filter, branches, showChartMenu = true, yearData = [], chartTitle }) => {
  const { i18n } = useTranslation();
  const {
    chartData,
    setChartData,
    chartOptions: chartOptionsFromHook,
    setChartOptions,
    chartRef
  } = useBasicGraphCard(t);

  const chartOptions = chartOptionsFromHook;
  const [chartReady, setChartReady] = useState(false);
  const [showLabels, setShowLabels] = useState(true);
  const [showTableView, setShowTableView] = useState(false);
  const [tableViewModalOpen, setTableViewModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [nullDpModalContext, setNullDpModalContext] = useState(null);
  const [nullMarkerHover, setNullMarkerHover] = useState(null);

  useEffect(() => {
    if (dataViewData && configRows && branches && branches.length > 0) {
      setupStackedBarChartData(dataViewData, configRows, branches);
    } else {
      setChartReady(false);
    }
  }, [dataViewData, configRows, branches, filter, showLabels]);

  const setupStackedBarChartData = (dataViewData, configRows, branches) => {
    if (!dataViewData || typeof dataViewData !== 'object' || !configRows || configRows.length === 0 || !branches || branches.length === 0) {
      setChartReady(false);
      return;
    }

    const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const isMonthlyStructure = Object.keys(dataViewData).some(year => {
      const yearData = dataViewData[year];
      if (yearData && typeof yearData === 'object') {
        return Object.keys(yearData).some(key => monthOrder.includes(key));
      }
      return false;
    });

    const periodType = filter?.periodType || 'YEARLY';
    const useYearlyXAxis = periodType === 'YEARLY';
    const isMonthlyNullMarkers = periodType === 'MONTHLY' && !useYearlyXAxis;
    const nullValueHintText = t('ESGAnalytics.consolidatedNullValueHint.label') || '';

    const totalLabel = t('Total.label') || 'Total';
    const chartItems = [];
    configRows.forEach((row, rowIndex) => {
      if (row.useLevel1Equation && row.level1Equation?.expressionWithId) {
        const name = row.name || `Level1 Equation ${rowIndex + 1}`;
        if (name === totalLabel) return;
        chartItems.push({
          id: row.level1Equation.expressionWithId,
          itemType: 'equation',
          name,
          level: 'level1'
        });
      } else if (row.level1?.id) {
        const name = row.name || row.level1.name || row.level1.dataPointName || row.level1.metricName || `Level1 ${rowIndex + 1}`;
        if (name === totalLabel) return;
        chartItems.push({
          id: row.level1.id,
          itemType: row.level1.itemType || 'datapoint',
          name,
          level: 'level1'
        });
      }
    });

    if (chartItems.length === 0) {
      setChartReady(false);
      return;
    }

    // X-axis = periods (same as line diagram). Label format matches line graph.
    let allPeriodKeys = [];
    const labels = [];

    if (useYearlyXAxis) {
      const allYearsSet = isMonthlyStructure
        ? new Set(
          (() => {
            const set = new Set();
            Object.keys(dataViewData).forEach(year => {
              const yearData = dataViewData[year];
              if (yearData && typeof yearData === 'object') {
                Object.keys(yearData).forEach(period => {
                  if (monthOrder.includes(period)) set.add(year);
                });
              }
            });
            return set;
          })()
        )
        : new Set(Object.keys(dataViewData).filter(y => /^\d{4}$/.test(y)));
      const allYears = Array.from(allYearsSet).sort((a, b) => parseInt(a) - parseInt(b));
      allPeriodKeys = allYears;
      allYears.forEach(year => labels.push(year));
    } else {
      const allMonthsSet = new Set();
      Object.keys(dataViewData).forEach(year => {
        const yearData = dataViewData[year];
        if (yearData && typeof yearData === 'object') {
          if (isMonthlyStructure) {
            Object.keys(yearData).forEach(period => {
              if (monthOrder.includes(period)) allMonthsSet.add(`${year}-${period}`);
            });
          } else {
            Object.keys(yearData).forEach(period => {
              if (period !== 'YEARLY') allMonthsSet.add(`${year}-${period}`);
            });
          }
        }
      });
      allPeriodKeys = Array.from(allMonthsSet).sort((a, b) => {
        const [yearA, periodA] = a.split('-');
        const [yearB, periodB] = b.split('-');
        if (yearA !== yearB) return parseInt(yearA) - parseInt(yearB);
        if (monthOrder.includes(periodA) && monthOrder.includes(periodB)) {
          return monthOrder.indexOf(periodA) - monthOrder.indexOf(periodB);
        }
        return String(periodA).localeCompare(String(periodB));
      });
      // Same as line diagram: "Jan 2024", "Feb 2024" via Intl.DateTimeFormat
      allPeriodKeys.forEach(periodKey => {
        const [year, period] = periodKey.split('-');
        if (monthOrder.includes(period)) {
          const monthIndex = monthOrder.indexOf(period);
          const date = new Date(parseInt(year), monthIndex, 1);
          const monthName = new Intl.DateTimeFormat(i18n.language, { month: 'short' }).format(date);
          labels.push(`${monthName} ${year}`);
        } else {
          labels.push(`${period} ${year}`);
        }
      });
    }

    if (allPeriodKeys.length === 0) {
      setChartReady(false);
      return;
    }

    const datasets = [];
    const colors = [
      { backgroundColor: '#0072B2CC' },
      { backgroundColor: '#D55E00CC' },
      { backgroundColor: '#009E73CC' },
      { backgroundColor: '#CC79A7CC' },
      { backgroundColor: '#56B4E9CC' },
      { backgroundColor: '#F0E442CC' },
      { backgroundColor: '#8B0000CC' },
      { backgroundColor: '#006400CC' },
      { backgroundColor: '#8B4513CC' },
      { backgroundColor: '#000080CC' },
      { backgroundColor: '#DAA520CC' },
      { backgroundColor: '#FF00FFCC' },
    ];


    const getConsolidatedForBranch = (periodData, item, branch) => {
      if (!periodData || typeof periodData !== 'object') {
        return { value: null, isExplicitNull: false, hasNullDps: false };
      }
      const branchId = branch.branchId;
      if (item.itemType === 'equation' && periodData.equations?.length) {
        const equation = periodData.equations.find((eq) =>
          [eq.equation, eq.expressionWithId, eq.id].some(
            (id) => id != null && String(id) === String(item.id)
          )
        );
        if (!equation) return { value: null, isExplicitNull: false, hasNullDps: false };
        const branchData = equation.branchConsolidated?.find((b) => b.branchId === branchId);
        if (!branchData) return { value: null, isExplicitNull: false, hasNullDps: false };
        if (branchData.value === null) {
          return {
            value: null,
            isExplicitNull: true,
            hasNullDps: branchData.hasNullDps === true
          };
        }
        return {
          value: branchData.value,
          isExplicitNull: false,
          hasNullDps: branchData.hasNullDps === true
        };
      }
      if (item.itemType === 'metric' || item.itemType === 'datapoint' || !item.itemType) {
        const metric = (periodData.metrics || []).find((m) =>
          [m.metricId, m.id].some((id) => id != null && String(id) === String(item.id))
        );
        if (metric) {
          const branchData = metric.branchConsolidated?.find((b) => b.branchId === branchId);
          if (!branchData) return { value: null, isExplicitNull: false, hasNullDps: false };
          if (branchData.value === null) {
            return {
              value: null,
              isExplicitNull: true,
              hasNullDps: branchData.hasNullDps === true
            };
          }
          return {
            value: branchData.value,
            isExplicitNull: false,
            hasNullDps: branchData.hasNullDps === true
          };
        }
      }
      const datapoint = (periodData.datapoints || []).find((dp) =>
        [dp.dataPointId, dp.id].some((id) => id != null && String(id) === String(item.id))
      );
      if (datapoint) {
        const branchData = datapoint.branchConsolidated?.find((b) => b.branchId === branchId);
        if (!branchData) return { value: null, isExplicitNull: false, hasNullDps: false };
        if (branchData.value === null) {
          return {
            value: null,
            isExplicitNull: true,
            hasNullDps: branchData.hasNullDps === true
          };
        }
        return {
          value: branchData.value,
          isExplicitNull: false,
          hasNullDps: branchData.hasNullDps === true
        };
      }
      return { value: null, isExplicitNull: false, hasNullDps: false };
    };

    const extractValueFromPeriodData = (periodData, item, branch) =>
      getConsolidatedForBranch(periodData, item, branch).value;

    const extractUomFromPeriodData = (periodData, item) => {
      if (!periodData || typeof periodData !== 'object') return '';
      if (item.itemType === 'equation' && periodData.equations?.length) {
        const equation = periodData.equations.find((eq) =>
          [eq.equation, eq.expressionWithId, eq.id].some((id) => id != null && String(id) === String(item.id))
        );
        return (equation?.uom && String(equation.uom).trim()) || '';
      }
      if (item.itemType === 'metric' || item.itemType === 'datapoint') {
        const metric = (periodData.metrics || []).find((m) =>
          [m.metricId, m.id].some((id) => id != null && String(id) === String(item.id))
        );
        if (metric?.uom) return String(metric.uom).trim();
        const datapoint = (periodData.datapoints || []).find((dp) =>
          [dp.dataPointId, dp.id].some((id) => id != null && String(id) === String(item.id))
        );
        return (datapoint?.uom && String(datapoint.uom).trim()) || '';
      }
      const metric = (periodData.metrics || []).find((m) =>
        [m.metricId, m.id].some((id) => id != null && String(id) === String(item.id))
      );
      if (metric?.uom) return String(metric.uom).trim();
      const datapoint = (periodData.datapoints || []).find((dp) =>
        [dp.dataPointId, dp.id].some((id) => id != null && String(id) === String(item.id))
      );
      return (datapoint?.uom && String(datapoint.uom).trim()) || '';
    };

    const getPeriodDataForKey = (periodKey) => {
      if (useYearlyXAxis) {
        const year = periodKey;
        const yearData = dataViewData[year];
        if (!yearData || typeof yearData !== 'object') return null;
        if (!isMonthlyStructure) {
          return yearData['YEARLY'] || (yearData.metrics || yearData.datapoints ? yearData : null) || Object.values(yearData)[0] || null;
        }
        return null;
      }
      const [y, period] = periodKey.split('-');
      const yd = dataViewData[y];
      return (yd && yd[period]) || null;
    };

    /** Period payload for UOM when yearly axis aggregates monthly buckets (getPeriodDataForKey is null per year). */
    const getPeriodDataForUomResolution = (periodKey) => {
      const direct = getPeriodDataForKey(periodKey);
      if (direct) return direct;
      if (useYearlyXAxis && isMonthlyStructure) {
        const year = periodKey;
        const yearData = dataViewData[year];
        if (!yearData || typeof yearData !== 'object') return null;
        for (const m of monthOrder) {
          if (yearData[m] && typeof yearData[m] === 'object') {
            return yearData[m];
          }
        }
      }
      return null;
    };

    const getSeriesUomForItem = (item) => {
      const uoms = new Set();
      allPeriodKeys.forEach((periodKey) => {
        const periodData = getPeriodDataForUomResolution(periodKey);
        if (!periodData) return;
        const u = extractUomFromPeriodData(periodData, item);
        if (u) uoms.add(u);
      });
      if (uoms.size === 0) return '';
      return Array.from(uoms).sort().join(' / ');
    };

    // One dataset per (metric, branch). Grouped bars: for each month, one bar per entity side by side. Exclude Total branch.
    const branchesForChart = branches.filter(b => (b.branchName || '') !== totalLabel);
    chartItems.forEach((item, itemIndex) => {
      branchesForChart.forEach((branch, branchIndex) => {
        const data = [];
        const color = colors[branchIndex % colors.length];

        const nullPointFlags = [];
        const nullPointKinds = [];

        allPeriodKeys.forEach(periodKey => {
          let value = null;
          const year = useYearlyXAxis ? periodKey : periodKey.split('-')[0];
          const yearData = dataViewData[year];

          if (useYearlyXAxis && isMonthlyStructure && yearData && typeof yearData === 'object') {
            let sum = 0;
            let hasAny = false;
            Object.keys(yearData).filter(p => monthOrder.includes(p)).forEach(m => {
              const periodData = yearData[m];
              const v = extractValueFromPeriodData(periodData, item, branch);
              if (v != null) { sum += parseFloat(v) || 0; hasAny = true; }
            });
            value = hasAny ? sum : null;
            nullPointFlags.push(false);
            nullPointKinds.push(null);
          } else {
            const periodData = getPeriodDataForKey(periodKey);
            const consolidated = getConsolidatedForBranch(periodData, item, branch);
            value = consolidated.value;
            const hasMarker = Boolean(
              isMonthlyNullMarkers && (consolidated.isExplicitNull || consolidated.hasNullDps)
            );
            nullPointFlags.push(hasMarker);
            nullPointKinds.push(
              !hasMarker ? null : (consolidated.isExplicitNull ? 'explicitNull' : 'hasNullDps')
            );
          }

          data.push(value != null ? parseFloat(value) || 0 : 0);
        });

        datasets.push({
          label: branch.branchName || `Entity ${branchIndex + 1}`,
          uom: getSeriesUomForItem(item),
          backgroundColor: color.backgroundColor,
          borderColor: 'transparent',
          borderWidth: 0,
          data,
          nullPointFlags,
          nullPointKinds,
          // Identifies the metric/equation + branch behind this series, for the null-marker breakdown popover
          branchId: branch.branchId,
          level1Item: {
            id: item.id,
            itemType: item.itemType,
            isEquation: item.itemType === 'equation',
            equationId: item.itemType === 'equation' ? item.id : null,
          },
        });
      });
    });

    if (datasets.length === 0) {
      setChartReady(false);
      return;
    }

    const yAxisUomTitle = (() => {
      const uoms = new Set();
      allPeriodKeys.forEach((periodKey) => {
        const periodData = getPeriodDataForUomResolution(periodKey);
        if (!periodData) return;
        chartItems.forEach((item) => {
          const u = extractUomFromPeriodData(periodData, item);
          if (u) uoms.add(u);
        });
      });
      if (uoms.size === 0) return '';
      return Array.from(uoms).sort().join(' / ');
    })();

    const cOption = {
      // Extra top padding when null markers are drawn so circles stay inside chart area above datalabels
      layout: { padding: { top: isMonthlyNullMarkers ? 36 : 20, bottom: 5, left: 5, right: 5 } },
      responsive: true,
      maintainAspectRatio: false,
      onClick: (event, _elements, chartInstance) => {
        if (!isMonthlyNullMarkers) return;
        const marker = findNullMarkerAtPoint(chartInstance, event.x, event.y);
        if (!marker) return;
        const { level1Item, branchId } = marker.dataset || {};
        if (!level1Item || level1Item.itemType === 'datapoint') return;
        const periodKey = allPeriodKeys[marker.dataIndex];
        const branch = branches.find((b) => b.branchId === branchId);
        // Use the widget's full configuration (not the single-slide config the carousel narrows
        // `configRows` to) so the matched row still carries its real eqExpressionWithId etc.
        const sourceConfigRows = allConfigRows || configRows;
        setNullDpModalContext({
          title: `${branch?.branchName || ''} - ${getPeriodLabel(periodKey)}`,
          period: periodKey,
          periodType: filter?.periodType || 'YEARLY',
          branchId,
          requestPayload: buildNullDatapointBreakdownPayload(sourceConfigRows, level1Item, branchId, periodKey, filter),
        });
      },
      onHover: (event, _elements, chartInstance) => {
        if (!isMonthlyNullMarkers) return;
        const marker = findNullMarkerAtPoint(chartInstance, event.x, event.y);
        if (chartInstance.canvas) {
          chartInstance.canvas.style.cursor = marker ? 'pointer' : '';
        }
        setNullMarkerHover((prev) => {
          if (!marker) return prev ? null : prev;
          const branchName = marker.dataset?.label || '';
          if (prev && prev.x === marker.x && prev.y === marker.cy && prev.branchName === branchName) return prev;
          return { x: marker.x, y: marker.cy, branchName };
        });
      },
      scales: {
        x: {
          stacked: false,
          barPercentage: 0.75,
          categoryPercentage: 0.8,
          ticks: { font: { size: 11 }, maxRotation: 45, minRotation: 45 },
          grid: { display: true, color: 'rgba(200, 200, 200, 0.2)' },
        },
        y: {
          stacked: false,
          title: {
            display: Boolean(yAxisUomTitle),
            text: yAxisUomTitle,
            font: { size: 11 },
            color: '#64748b',
          },
          ticks: { font: { size: 11 }, callback: (value) => Utils.formattedNumber(value) },
          grid: { display: true, color: 'rgba(200, 200, 200, 0.2)' },
          beginAtZero: true,
        },
      },
      plugins: {
        esgNullMarkers: { enabled: isMonthlyNullMarkers },
        legend: {
          position: 'bottom',
          align: 'start',
          labels: { font: { size: 11 }, boxWidth: 12, padding: 15, usePointStyle: true },
          display: true,
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem) => {
              if (
                isMonthlyNullMarkers
                && tooltipItem.dataset?.nullPointFlags?.[tooltipItem.dataIndex]
              ) {
                const markerKind = tooltipItem.dataset?.nullPointKinds?.[tooltipItem.dataIndex];
                if (markerKind === 'explicitNull') {
                  return ` ${tooltipItem.dataset.label}: — (${nullValueHintText})`;
                }
                const value = tooltipItem?.dataset?.data[tooltipItem.dataIndex] ?? 0;
                const uom = tooltipItem.dataset.uom;
                const num = Utils.formattedNumber(value);
                const suffix = uom ? ` ${uom}` : '';
                return ` ${tooltipItem.dataset.label}: ${num}${suffix} (${nullValueHintText})`;
              }
              const value = tooltipItem?.dataset?.data[tooltipItem.dataIndex] ?? 0;
              const uom = tooltipItem.dataset.uom;
              const num = Utils.formattedNumber(value);
              const suffix = uom ? ` ${uom}` : '';
              return ` ${tooltipItem.dataset.label}: ${num}${suffix}`;
            },
          },
        },
        datalabels: {
          display: (context) => {
            if (!showLabels) return false;
            // Show value whenever bar is non-zero; red "!" for unreported/explicit-null is drawn separately
            // (do not hide labels when nullPointFlags — user needs value + indicator together for hasNullDps).
            return (context.dataset.data[context.dataIndex] ?? 0) !== 0;
          },
          color: '#1e293b',
          font: { size: 10, weight: '600' },
          anchor: 'end',
          align: 'center',
          offset: 2,
          clamp: true,
          rotation: -90,
          formatter: (value) => (value === 0 ? '' : Utils.formattedNumber(value)),
        },
      },
    };

    setChartOptions(cOption);
    setChartData({ labels, datasets });
    setChartReady(true);
  };

  const handleToggleLabels = () => setShowLabels(!showLabels);

  const getPeriods = () => {
    if (yearData?.length > 0) return yearData;
    if (!dataViewData || typeof dataViewData !== 'object') return [];
    const periods = [];
    const periodType = filter?.periodType || 'YEARLY';
    const monthOrder = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    Object.keys(dataViewData).sort((a, b) => parseInt(b) - parseInt(a)).forEach(year => {
      const yearData = dataViewData[year];
      if (yearData && typeof yearData === 'object') {
        if (periodType === 'YEARLY') {
          periods.push(year);
        } else {
          Object.keys(yearData).sort((a, b) => monthOrder.includes(a) && monthOrder.includes(b) ? monthOrder.indexOf(a) - monthOrder.indexOf(b) : a.localeCompare(b)).forEach(period => {
            periods.push(`${year}-${period}`);
          });
        }
      }
    });
    return periods;
  };

  const processEntityLevelData = () => {
    const hasEntities = (filter.parentEntities?.length > 0) || (filter.childEntities?.length > 0);
    if (!hasEntities || !branches?.length || !configRows?.length || !dataViewData) {
      return { entities: [], level1Items: [], dataByEntityAndPeriod: {} };
    }

    const selectedEntityIds = [...(filter.parentEntities || []), ...(filter.childEntities || [])];
    const selectedEntities = branches.filter(b => selectedEntityIds.includes(b.branchId) && b.branchName !== t('Total.label'));
    if (selectedEntities.length === 0) return { entities: [], level1Items: [], dataByEntityAndPeriod: {} };

    const level1Items = [];
    configRows.forEach(configRow => {
      if (configRow.useLevel1Equation && configRow.level1Equation?.expressionWithId) {
        level1Items.push({ id: configRow.level1Equation.expressionWithId, itemType: 'equation', name: configRow.name || 'Equation', label: configRow.name || '', isEquation: true, equationId: configRow.level1Equation.expressionWithId });
      } else if (configRow.level1?.id) {
        level1Items.push({ id: configRow.level1.id, itemType: configRow.level1.itemType || 'metric', name: configRow.level1.name || configRow.level1.metricName || configRow.level1.dataPointName || '', label: configRow.name || '' });
      }
    });
    if (level1Items.length === 0) return { entities: [], level1Items: [], dataByEntityAndPeriod: {} };

    const dataByEntityAndPeriod = {};
    const periods = getPeriods();
    const periodType = filter?.periodType || 'YEARLY';

    periods.forEach((period, periodIndex) => {
      let yearData = null;
      let periodData = null;
      if (periodType === 'YEARLY') {
        yearData = dataViewData[period];
        periodData = yearData?.['YEARLY'] || (yearData && typeof yearData === 'object' ? Object.values(yearData)[0] : null);
      } else {
        const [year, periodKey] = period.split('-');
        yearData = dataViewData[year];
        periodData = yearData?.[periodKey] || null;
      }
      if (!periodData || typeof periodData !== 'object') return;

      const addBranchValue = (itemId, isEquation, branchId, value) => {
        if (!dataByEntityAndPeriod[branchId]) dataByEntityAndPeriod[branchId] = {};
        if (!dataByEntityAndPeriod[branchId][periodIndex]) dataByEntityAndPeriod[branchId][periodIndex] = {};
        dataByEntityAndPeriod[branchId][periodIndex][itemId] = value;
      };

      (periodData.equations || []).forEach(eq => {
        const item = level1Items.find(
          i =>
            i.isEquation &&
            [eq.equation, eq.expressionWithId, eq.id].some(
              id => id != null && String(id) === String(i.equationId)
            )
        );
        if (!item) return;
        (eq.branchConsolidated || []).forEach(b => {
          if (b.value != null && selectedEntities.some(e => e.branchId === b.branchId)) addBranchValue(item.id, true, b.branchId, b.value);
        });
      });
      (periodData.metrics || []).forEach(m => {
        const item = level1Items.find(i => !i.isEquation && i.id === m.metricId);
        if (!item) return;
        (m.branchConsolidated || []).forEach(b => {
          if (b.value != null && selectedEntities.some(e => e.branchId === b.branchId)) addBranchValue(item.id, false, b.branchId, b.value);
        });
      });
      (periodData.datapoints || []).forEach(dp => {
        const item = level1Items.find(i => !i.isEquation && i.id === dp.dataPointId);
        if (!item) return;
        (dp.branchConsolidated || []).forEach(b => {
          if (b.value != null && selectedEntities.some(e => e.branchId === b.branchId)) addBranchValue(item.id, false, b.branchId, b.value);
        });
      });
    });

    return { entities: selectedEntities, level1Items, dataByEntityAndPeriod };
  };

  const entityLevelData = useMemo(() => processEntityLevelData(), [dataViewData, configRows, filter, branches, t]);
  const periods = useMemo(() => getPeriods(), [yearData, dataViewData, filter]);
  const hasEntityData = entityLevelData.entities.length > 0 && entityLevelData.level1Items.length > 0;

  const calculateTotals = () => {
    const totals = {};
    const periodType = filter?.periodType || 'YEARLY';
    if (!periods?.length || !entityLevelData?.level1Items?.length) return totals;
    periods.forEach((period, periodIndex) => {
      totals[periodIndex] = {};
      let periodData = null;
      if (periodType === 'YEARLY') {
        const yd = dataViewData?.[period];
        periodData = yd?.['YEARLY'] || (yd && typeof yd === 'object' ? Object.values(yd)[0] : null);
      } else {
        const [year, periodKey] = period.split('-');
        periodData = dataViewData?.[year]?.[periodKey] || null;
      }
      if (!periodData) {
        entityLevelData.level1Items.forEach(item => { totals[periodIndex][item.id] = 0; });
        return;
      }
      entityLevelData.level1Items.forEach(item => {
        let totalVal = 0;
        if (item.isEquation) {
          const foundEq = (periodData.equations || []).find(eq =>
            [eq.equation, eq.expressionWithId, eq.id].some(
              id => id != null && String(id) === String(item.equationId)
            )
          );
          if (foundEq?.total != null) totalVal = parseFloat(foundEq.total) || 0;
        }
        else if ((periodData.metrics || []).find(m => m.metricId === item.id)?.total != null) totalVal = parseFloat(periodData.metrics.find(m => m.metricId === item.id).total) || 0;
        else if ((periodData.datapoints || []).find(dp => dp.dataPointId === item.id)?.total != null) totalVal = parseFloat(periodData.datapoints.find(dp => dp.dataPointId === item.id).total) || 0;
        totals[periodIndex][item.id] = totalVal;
      });
    });
    return totals;
  };

  const totalsByPeriod = useMemo(() => (hasEntityData && periods.length > 0 ? calculateTotals() : {}), [entityLevelData, periods, dataViewData, filter, hasEntityData]);

  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(columnKey); setSortDirection('asc'); }
  };

  const getSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) return <BiSort className="ms-1" style={{ opacity: 0.3 }} />;
    return sortDirection === 'asc' ? <BiSortUp className="ms-1" /> : <BiSortDown className="ms-1" />;
  };

  const getSortedEntities = () => {
    if (!sortColumn || !hasEntityData || !entityLevelData?.entities?.length) return entityLevelData?.entities || [];
    return [...entityLevelData.entities].sort((a, b) => {
      if (sortColumn === 'entity') {
        const cmp = (a.branchName || '').toLowerCase().localeCompare((b.branchName || '').toLowerCase());
        return sortDirection === 'asc' ? cmp : -cmp;
      }
      const parts = sortColumn.split('-');
      const yearIndex = parseInt(parts[0]);
      const columnType = parts[parts.length - 1];
      const itemId = parts.slice(1, -1).join('-');
      const aData = entityLevelData.dataByEntityAndPeriod[a.branchId]?.[yearIndex]?.[itemId];
      const bData = entityLevelData.dataByEntityAndPeriod[b.branchId]?.[yearIndex]?.[itemId];
      const total = totalsByPeriod[yearIndex]?.[itemId] || 0;
      const aVal = aData != null && !isNaN(parseFloat(aData)) ? (columnType === 'percent' && total > 0 ? (parseFloat(aData) / total) * 100 : parseFloat(aData)) : -Infinity;
      const bVal = bData != null && !isNaN(parseFloat(bData)) ? (columnType === 'percent' && total > 0 ? (parseFloat(bData) / total) * 100 : parseFloat(bData)) : -Infinity;
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  };

  const sortedEntities = useMemo(() => (hasEntityData ? getSortedEntities() : []), [sortColumn, sortDirection, entityLevelData, totalsByPeriod, hasEntityData]);

  const getPeriodLabel = (period) => (filter?.periodType === 'YEARLY' ? period : period.replace('-', ' '));

  const handleDownload = () => {
    if (chartRef?.current) {
      const canvas = chartRef.current.canvas;
      const ctx = canvas.getContext('2d');
      ctx.save();
      const headingText = chartTitle || t('ESGAnalytics.byOrganizationUnit.label') || 'Consolidated Performance';
      const headingHeight = 56;
      const downloadCanvas = document.createElement('canvas');
      const dCtx = downloadCanvas.getContext('2d');
      downloadCanvas.width = canvas.width;
      downloadCanvas.height = canvas.height + headingHeight;
      dCtx.fillStyle = '#ffffff';
      dCtx.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
      dCtx.fillStyle = '#1e293b';
      dCtx.font = '700 24px Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
      dCtx.textBaseline = 'middle';
      dCtx.textAlign = 'center';
      dCtx.fillText(headingText, downloadCanvas.width / 2, headingHeight / 2);
      dCtx.drawImage(canvas, 0, headingHeight, canvas.width, canvas.height);
      const link = document.createElement('a');
      link.href = downloadCanvas.toDataURL('image/jpeg', 0.9);
      link.download = `${(chartTitle || t('ESGAnalytics.byOrganizationUnit.label') || 'consolidated-performance').replace(/[^a-zA-Z0-9-_]/g, '-')}.jpg`;
      link.click();
      ctx.restore();
    }
  };

  if (!chartReady || !dataViewData || !branches?.length) return null;

  return (
    <>
      <Card className="esg-analytics-org-unit-graph-card mb-0 pt-0">
        <Card.Header className="d-flex justify-content-between align-items-center border-0 pb-0">
          <h5 className="mb-0 d-flex align-items-center gap-2">
            {chartTitle || t('ESGAnalytics.byOrganizationUnit.label') || 'Consolidated Performance'}
            {/* <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="esg-consolidated-performance-tooltip">
                  {t('ESGAnalytics.byOrganizationUnitInfo.label') || 'Shows the total value across the organisation'}
                </Tooltip>
              }
            >
              <span className="esg-stacked-bar-info-icon" aria-label={t('ESGAnalytics.byOrganizationUnitInfo.label') || 'Shows the total value across the organisation'}>
                <BiInfoCircle size={16} />
              </span>
            </OverlayTrigger> */}
          </h5>
          {showChartMenu && (
            <ChartMenu
              chartId="esg-analytics-org-unit-chart"
              onToggleLabels={handleToggleLabels}
              onMaximize={() => { }}
              onViewTable={() => setTableViewModalOpen(true)}
              onDownload={handleDownload}
              showLabels={showLabels}
              isMaximized={false}
              showTableView={tableViewModalOpen}
              chartType="STACKED BAR"
              showGetInfo={false}
              showToggleLabels={true}
              showToggleView={false}
              showMaximize={false}
              showDataSources={false}
              showViewTable={false}
              showDownload={true}
              showDeactivate={false}
            />
          )}
        </Card.Header>
        <Card.Body>
          <div className="stacked-bar-graph-container" onMouseLeave={() => setNullMarkerHover(null)}>
            {chartReady && chartData && chartOptions && (
              <Bar data={chartData} options={chartOptions} ref={chartRef} plugins={[ChartDataLabels, ESG_EXCLAMATION_NULL_MARKER_PLUGIN]} />
            )}
            {nullMarkerHover && (
              <div
                className="esg-stacked-bar-null-marker-tooltip"
                style={{ left: nullMarkerHover.x, top: nullMarkerHover.y }}
              >
                {nullMarkerHover.branchName}
              </div>
            )}
          </div>
          {filter?.periodType === 'MONTHLY' && (
            <div
              className="esg-stacked-bar-null-legend d-flex align-items-center gap-2 mt-2"
              role="note"
            >
              <span className="esg-stacked-bar-null-legend__icon" aria-hidden="true">
                !
              </span>
              <span className="esg-stacked-bar-null-legend__text">
                {t('ESGAnalytics.unreportedDataPointsLegend.label') || 'Contains unreported data points'}
              </span>
            </div>
          )}
        </Card.Body>
      </Card>

      {showChartMenu && (
        <Modal show={tableViewModalOpen} onHide={() => setTableViewModalOpen(false)} size="xl" centered className="NewDashboard_modal">
          <Modal.Header closeButton>
            <Modal.Title as="h5">{chartTitle || t('ESGAnalytics.byOrganizationUnit.label') || 'Consolidated Performance'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="p-4">
              {!hasEntityData ? (
                <div className="text-center text-muted py-4">{t('ESGAnalytics.selectEntitiesToView.label') || 'Please select parent or child entities to view entity level data.'}</div>
              ) : periods.length === 0 || entityLevelData.level1Items.length === 0 ? (
                <div className="text-center text-muted py-4">{t('noDataAvailable.label') || 'No data available'}</div>
              ) : (
                <Table responsive bordered className="organization-unit-table 2">
                  <thead>
                    <tr>
                      <th rowSpan={3} className="esg-sortable-header">
                        <span className="d-inline-flex align-items-center cursor-pointer" onClick={() => handleSort('entity')}>
                          {t('Entity.label') || 'Entity'}
                          {getSortIcon('entity')}
                        </span>
                      </th>
                      {periods.map(period => (
                        <th key={period} className="text-center" colSpan={entityLevelData.level1Items.length * 2}>{getPeriodLabel(period)}</th>
                      ))}
                    </tr>
                    <tr>
                      {periods.map((period, periodIndex) =>
                        entityLevelData.level1Items.map((item, itemIndex) => (
                          <th key={`${period}-${itemIndex}-header`} className="text-center" colSpan={2}>{item.label || item.name}</th>
                        ))
                      )}
                    </tr>
                    <tr>
                      {periods.map((period, periodIndex) =>
                        entityLevelData.level1Items.map((item) => (
                          <React.Fragment key={`${period}-${item.id}-subheader`}>
                            <th className="text-center esg-sortable-header">
                              <span className="d-inline-flex align-items-center justify-content-center cursor-pointer" onClick={() => handleSort(`${periodIndex}-${item.id}-percent`)}>% {getSortIcon(`${periodIndex}-${item.id}-percent`)}</span>
                            </th>
                            <th className="text-center esg-sortable-header">
                              <span className="d-inline-flex align-items-center justify-content-center cursor-pointer" onClick={() => handleSort(`${periodIndex}-${item.id}-value`)}>{t('Value.label') || 'Value'} {getSortIcon(`${periodIndex}-${item.id}-value`)}</span>
                            </th>
                          </React.Fragment>
                        ))
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedEntities.map((entity) => (
                      <tr key={entity.branchId}>
                        <td className="fw-bold">{entity.branchName}</td>
                        {periods.map((period, periodIndex) =>
                          entityLevelData.level1Items.map((item) => {
                            const value = entityLevelData.dataByEntityAndPeriod[entity.branchId]?.[periodIndex]?.[item.id];
                            const total = totalsByPeriod[periodIndex]?.[item.id] || 0;
                            const percentage = value != null && !isNaN(parseFloat(value)) && total > 0 ? Utils.roundedPercentage((parseFloat(value) / total) * 100) + '%' : '-';
                            const displayValue = value != null ? Utils.formattedNumber(value) : '-';
                            return (
                              <React.Fragment key={`${period}-${item.id}`}>
                                <td className="text-center">{percentage}</td>
                                <td className="text-center">{displayValue}</td>
                              </React.Fragment>
                            );
                          })
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setTableViewModalOpen(false)}>{t('Close.label') || 'Close'}</Button>
          </Modal.Footer>
        </Modal>
      )}

      {nullDpModalContext && (
        <ESGAnalyticsDPBreakdownModal
          context={nullDpModalContext}
          onHide={() => setNullDpModalContext(null)}
        />
      )}
    </>
  );
};

export default withTranslation()(ESGAnalyticsStackedBarChart);
